import React from 'react';
import { DashboardStats } from '../../types/dashboard';

interface StatsCardProps {
  stats: DashboardStats;
}

const StatsCard: React.FC<{ type: keyof DashboardStats; stats: DashboardStats }> = ({ type, stats }) => {
  const getCardConfig = (type: keyof DashboardStats) => {
    switch (type) {
      case 'totalProducts':
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          ),
          label: 'Total de Produtos',
          description: 'Produtos cadastrados',
          bgColor: 'bg-coffee-100',
          iconColor: 'text-coffee-600',
          value: stats.totalProducts.toString(),
          // change: '+12%',
          changeType: 'positive' as const,
        };
      case 'totalReviews':
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.118 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          ),
          label: 'Avaliações',
          description: 'Total de avaliações',
          bgColor: 'bg-accent-100',
          iconColor: 'text-accent-600',
          value: stats.totalReviews.toString(),
          // change: '+8%',
          changeType: 'positive' as const,
        };
      case 'totalFavorites':
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          ),
          label: 'Favoritos',
          description: 'Adicionados aos favoritos',
          bgColor: 'bg-error-100',
          iconColor: 'text-error-600',
          value: stats.totalFavorites.toString(),
          // change: '+15%',
          changeType: 'positive' as const,
        };
      case 'averageRating':
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
          label: 'Avaliação Média',
          description: 'Nota média geral',
          bgColor: 'bg-success-100',
          iconColor: 'text-success-600',
          value: stats.averageRating.toFixed(1),
          // change: '+0.2',
          changeType: 'positive' as const,
        };
      default:
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          ),
          label: 'Estatística',
          description: 'Informação geral',
          bgColor: 'bg-neutral-100',
          iconColor: 'text-neutral-600',
          value: '0',
          change: '0%',
          changeType: 'neutral' as const,
        };
    }
  };

  const config = getCardConfig(type);

  return (
    <div className="stat-card group hover:shadow-large transition-all duration-300">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 ${config.bgColor} rounded-xl group-hover:scale-110 transition-transform duration-300`}>
          <div className={config.iconColor}>
            {config.icon}
          </div>
        </div>
        
        {/* Indicador de mudança */}
        <div className="flex items-center space-x-1">
          <span className={`text-xs font-semibold ${
            config.changeType === 'positive' ? 'text-success-600' : 
            'text-neutral-600'
          }`}>
            {config.change}
          </span>
          {config.changeType === 'positive' && (
            <svg className="w-3 h-3 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          )}
          {config.changeType === 'neutral' && (
            <svg className="w-3 h-3 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
            </svg>
          )}
        </div>
      </div>
      
      <div>
        <h3 className="text-sm font-medium text-neutral-600 mb-1">
          {config.label}
        </h3>
        <p className="text-3xl font-bold text-neutral-900 mb-1">
          {config.value}
        </p>
        <p className="text-xs text-neutral-500">
          {config.description}
        </p>
      </div>

      {/* Linha decorativa na parte inferior */}
      <div className={`absolute bottom-0 left-0 right-0 h-1 ${config.bgColor} rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
    </div>
  );
};

const StatsCards: React.FC<StatsCardProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatsCard type="totalProducts" stats={stats} />
      <StatsCard type="totalFavorites" stats={stats} />
      <StatsCard type="totalReviews" stats={stats} />
      <StatsCard type="averageRating" stats={stats} />
    </div>
  );
};

export default StatsCards; 